import { TrafficSourceCodeEnum } from '@metapic-apps/shared'

import type { AdvertiserServerType, AdvertiserType } from '@/lib/types/advertiser.types'

export const normalizeAdvertiser = (data: AdvertiserServerType): AdvertiserType => {
  return {
    id: data.id,
    feedName: data.feed_name,
    slug: data.slug,
    feedProvider: data.feed_provider,
    languageIsoCode: data.language_iso_code,
    currencyIsoCode: data.currency_iso_code,
    locale: data.locale,
    directLink: data.direct_link,
    deeplinkable: data.deeplinkable,
    autoUpdate: data.autoupdate,
    url: data.url,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    storeProviderAccountId: data.store_provider_account_id,
    lastCorrectSync: data.last_correct_sync,
    overridePaymentPercentage: data.override_payment_percentage,
    programId: data.program_id,
    deletedAt: data.deleted_at,
    revenueText: data.revenue_text,
    revenueOfCommision: data.revenue_of_commision,
    estimatedReturnRate: data.estimated_return_rate,
    earningsReturnRate: data.earnings_return_rate,
    minimumReturnRate: data.minimum_return_rate,
    hidden: data.hidden,
    tdEarning: data.td_earning,
    metapicInvoicing: data.metapic_invoicing,
    logoUrl: data.logo_url,
    status: data.status,
    showOrders: data.show_orders,
    ignoreRule6: data.ignore_rule_6,
    stripeCustomerId: data.stripe_customer_id,
    advertiserUseCpa: data.advertiser_use_cpa,
    isDemo: data.is_demo,
    demoId: data.demo_id,
    showForMarket: data.show_for_market,
    invoiceConfig: {
      emails: data.invoice_config.emails,
      dueDays: data.invoice_config.due_days,
      reference: data.invoice_config.reference,
      vatNumber: data.invoice_config.vat_number,
      companyName: data.invoice_config.company_name,
      invoicingAddress: data.invoice_config.invoicing_address,
    },
    sendInvoice: data.send_invoice,
    acceptOrdersPeriod: data.accept_orders_period,
    autoAcceptInstagramStories: data.auto_accept_instagram_stories,
    dontCountActionsOnInvoice: data.dont_count_actions_on_invoice,
    stripeSubscriptionStatus: data.stripe_subscription_status,
    paymentType: data.payment_type,
    card: data.card?.card
      ? {
          brand: data.card.card.brand,
          last4: data.card.card.last4,
          expMonth: data.card.card.exp_month,
          expYear: data.card.card.exp_year,
        }
      : undefined,
    stripeSubscriptionId: data.stripe_subscription_id,
    monthlyBudget: data.monthly_budget,
    autoImported: data.auto_imported,
    ignoreRemoteEarnings: data.ignore_remote_earnings,
    storeType: data.store_type,
    storeGroupId: data.store_group_id,
    isDirect: data.is_direct,
    urls: data.urls?.map((url) => ({
      id: url.id,
      storeId: url.store_id,
      endHost: url.end_host,
      startUrl: url.start_url,
      createdAt: url.created_at,
      updatedAt: url.updated_at,
    })),
    currencyObj: {
      code: data.currency_obj.code,
      name: data.currency_obj.name,
      symbol: data.currency_obj.symbol,
      subunit: data.currency_obj.subunit,
      isBefore: data.currency_obj.is_before,
      ratioToEur: data.currency_obj.ratio_to_eur,
    },
    storeCategories: data.store_categories?.map((category) => ({
      id: category.id,
      name: category.name,
    })),
    trafficSourcesCosts: data.traffic_sources_costs?.map((cost) => ({
      id: cost.id,
      storeId: cost.store_id,
      source: cost.source,
      cpc: cost.cpc,
      invoiceCpc: cost.invoice_cpc,
      cpa: cost.cpa,
      invoiceCpa: cost.invoice_cpa,
      createdAt: cost.created_at,
      updatedAt: cost.updated_at,
    })),
    storeGroups: data.store_groups?.map((group) => ({
      id: group.id,
      name: group.name,
      key: group.key,
      locale: group.locale,
      lang: group.lang,
      currency: group.currency,
      paymentLimit: group.payment_limit,
      paidToAccount: group.paid_to_account,
      ourReference: group.our_reference,
      pivot: {
        storeId: group.pivot.store_id,
        storeGroupId: group.pivot.store_group_id,
      },
    })),
    userPermissions: data.user_permissions?.map((permission) => ({
      id: permission.id,
      storeId: permission.store_id,
      userId: permission.user_id,
      editableClientId: permission.editable_client_id,
      email: permission.email,
      emailConfig: permission.email_config,
      config: {
        hide: permission.config.hide,
      },
      code: permission.code,
      createdAt: permission.created_at,
      updatedAt: permission.updated_at,
    })),
    cpms: data.cpms ? mapCpmsToRecords(data.cpms) : undefined,
  }
}

export type SalesPixelSettings = {
  latestOrderAt?: string
  latestOrderValue?: number
  testOrderUrl: string
  organizationId: number
  programId: number
  eventId: number
  currency: string
}

export const normalizeAdvertiserOrderSalesPixel = (data: {
  latest_order_at?: string
  order_value?: number
  test_order_url: string
  organization_id: number
  program_id: number
  event_id: number
  currency: string
}): SalesPixelSettings => ({
  latestOrderAt: data.latest_order_at,
  latestOrderValue: data.order_value,
  testOrderUrl: data.test_order_url,
  organizationId: data.organization_id,
  programId: data.program_id,
  eventId: data.event_id,
  currency: data.currency,
})

export type CpmFormValues = Partial<Record<TrafficSourceCodeEnum, number>>

export type CpmValues = Partial<Record<TrafficSourceCodeEnum, number>>

export const mapCpmsToRecords = (
  cpms: AdvertiserServerType['cpms'] | undefined,
): { values: CpmValues; defaults: CpmValues } => {
  const parse = (value: string | null | undefined): number | undefined =>
    value != null && value !== '' ? parseFloat(value) : undefined

  const values: CpmValues = {}
  const defaults: CpmValues = {}

  for (const code of Object.values(TrafficSourceCodeEnum)) {
    const entry = cpms?.find((cpm) => cpm.traffic_source_code === code)
    values[code] = parse(entry?.cpm)
    defaults[code] = parse(entry?.default_cpm)
  }

  return { values, defaults }
}

export type UpdateCpmsPayload = {
  cpms: Record<string, string>
}

export const serializeAdvertiserCpms = (
  values: CpmFormValues,
  originalCpms: CpmValues = {},
): UpdateCpmsPayload => {
  const cpms: Record<string, string> = {}
  for (const code of Object.keys(values) as TrafficSourceCodeEnum[]) {
    const value = values[code]
    if (value != null && value !== originalCpms[code]) {
      cpms[code] = String(value)
    }
  }
  return { cpms }
}
